import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * WebSocket-based workflow hook for Bill Processing WS Agent (Workflow 22)
 * Pure WebSocket - no polling fallback
 */
export const useBillWSWorkflow = (workflowId, token, initialRunId = null) => {
  // Core state
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [runId, setRunId] = useState(initialRunId);
  const [executionStatus, setExecutionStatus] = useState('idle');
  const [currentStep, setCurrentStep] = useState(null);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);

  // Refs
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const runIdRef = useRef(runId);
  const initialLoadCompleteRef = useRef(false);  // Track if initial batch load is done

  // Keep runIdRef in sync
  useEffect(() => {
    runIdRef.current = runId;
  }, [runId]);

  // Process incoming WebSocket message
  const processWsMessage = useCallback((data) => {
    console.log('📨 BillWS Message:', data.type, data);

    switch (data.type) {
      case 'connection_established':
        setWsConnected(true);
        break;

      case 'existing_messages':
        // Batch load of all existing messages - single state update (faster than one-by-one)
        if (data.messages && Array.isArray(data.messages)) {
          console.log(`📥 BillWS: Received ${data.messages.length} existing messages in batch`);
          const formattedMessages = data.messages.map((msg, idx) => {
            let isUIComponent = false;
            let content = msg.content;

            if (msg.message_type === 'workflow_step') {
              try {
                const parsed = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                if (parsed?.status === 'ui_ready' && parsed?.component_definition) {
                  content = parsed;
                  isUIComponent = true;
                }
              } catch (e) {}
            }

            // FIX: message_id can be empty string '' from backend - treat as missing
            const messageId = msg.message_id && msg.message_id.trim() !== ''
              ? msg.message_id
              : `batch_${idx}_${msg.timestamp || Date.now()}`;

            return {
              id: messageId,
              type: msg.message_type,
              content: content,
              timestamp: msg.timestamp || new Date().toISOString(),
              step_name: msg.step_name,
              tool_name: msg.tool_name,
              isUIComponent
            };
          });

          // Single state update with all messages, already sorted by timestamp from backend
          // Mark initial load as complete BEFORE setting messages to prevent auto-scroll on batch
          initialLoadCompleteRef.current = true;
          setMessages(formattedMessages.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)));
          console.log(`✅ BillWS: Initial batch load complete, ${formattedMessages.length} messages`);
        }
        break;

      case 'step_started':
        setCurrentStep(data.step_name);
        setExecutionStatus('running');
        break;

      case 'step_completed':
        if (data.structured_output?.component_definition) {
          setMessages(prev => [...prev, {
            id: `ui_${Date.now()}`,
            type: 'assistant',
            content: data.structured_output,
            timestamp: new Date().toISOString(),
            step_name: data.step_name,
            isUIComponent: true
          }]);
        }
        break;

      case 'step_failed':
        setMessages(prev => [...prev, {
          id: `error_${Date.now()}`,
          type: 'error',
          content: `Failed: ${data.step_name} - ${data.error_message}`,
          timestamp: new Date().toISOString()
        }]);
        setExecutionStatus('failed');
        break;

      case 'chat_message':
        // FIX: message_id can be empty string '' from backend - treat as missing
        const chatMsgId = data.message_id && data.message_id.trim() !== ''
          ? data.message_id
          : `realtime_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        const msgData = {
          id: chatMsgId,
          type: data.message_type,
          content: data.content,
          timestamp: data.timestamp || new Date().toISOString(),
          step_name: data.step_name,
          tool_name: data.tool_name,
          isUIComponent: false
        };

        // Check for UI component
        if (data.message_type === 'workflow_step') {
          try {
            const parsed = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
            if (parsed?.status === 'ui_ready' && parsed?.component_definition) {
              msgData.content = parsed;
              msgData.isUIComponent = true;
            }
          } catch (e) {}
        }

        setMessages(prev => {
          // Duplicate check by ID
          if (prev.some(m => m.id === msgData.id)) {
            console.log(`⏭️ BillWS: Skipping duplicate message ${msgData.id}`);
            return prev;
          }
          // Also check by content+timestamp to catch duplicates with different IDs
          const isDuplicateContent = prev.some(m =>
            m.timestamp === msgData.timestamp &&
            m.type === msgData.type &&
            JSON.stringify(m.content) === JSON.stringify(msgData.content)
          );
          if (isDuplicateContent) {
            console.log(`⏭️ BillWS: Skipping duplicate message by content`);
            return prev;
          }
          console.log(`📨 BillWS: Adding new realtime message ${msgData.id}`);
          // DON'T sort - just append. Real-time messages arrive in order from server.
          // Sorting causes visual "jumping" when timestamps differ slightly.
          return [...prev, msgData];
        });
        break;

      case 'workflow_status':
        setExecutionStatus(data.status);
        if (data.current_step) setCurrentStep(data.current_step);
        // Stop loading for any non-running status
        if (data.status === 'awaiting_user' || data.status === 'completed' || data.status === 'failed') {
          setIsLoading(false);
          console.log('📥 BillWS: Status', data.status, '- stopping loading indicator');
        }
        break;

      case 'workflow_completed':
        setExecutionStatus(data.success ? 'completed' : 'failed');
        setIsLoading(false);

        // Add completion message with bill URL if available
        if (data.success && data.result) {
          const completionMsg = {
            id: `completion_${Date.now()}`,
            type: 'assistant',
            content: data.result.message || 'Workflow completed successfully!',
            timestamp: new Date().toISOString(),
            isUIComponent: false,
            netsuite_bill_url: data.result.netsuite_bill_url,
            netsuite_bill_id: data.result.netsuite_bill_id
          };
          console.log('✅ BillWS: Adding completion message with bill URL:', data.result.netsuite_bill_url);
          setMessages(prev => [...prev, completionMsg]);
        } else if (!data.success && data.error_message) {
          const errorMsg = {
            id: `error_${Date.now()}`,
            type: 'error',
            content: data.error_message,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => [...prev, errorMsg]);
        }
        break;

      case 'pong':
        // Keep-alive response
        break;

      default:
        console.log('Unknown message type:', data.type);
        break;
    }
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback((currentRunId) => {
    if (!token || !currentRunId) return;

    if (wsRef.current) {
      wsRef.current.close();
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/api/bill-processing-ws/ws/${currentRunId}?token=${token}`;

    console.log('🔌 BillWS Connecting:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('✅ BillWS WebSocket connected');
      setWsConnected(true);
      setError(null);

      // Send immediate ping to keep connection alive from the start
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
        console.log('📡 BillWS: Sent initial ping');
      }

      // Start ping interval (every 25 seconds - well within 60s backend timeout)
      pingIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
          console.log('📡 BillWS: Sent keepalive ping');
        }
      }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        processWsMessage(JSON.parse(event.data));
      } catch (e) {
        console.error('BillWS parse error:', e);
      }
    };

    ws.onerror = (event) => {
      console.error('❌ BillWS WebSocket error:', event);
      setError('WebSocket connection error');
    };

    ws.onclose = (event) => {
      console.log('🔌 BillWS WebSocket closed:', event.code, event.reason);
      setWsConnected(false);

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      // Reconnect if not intentional close
      if (event.code !== 1000 && runIdRef.current === currentRunId) {
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket(currentRunId);
        }, 3000);
      }
    };

    wsRef.current = ws;
  }, [token, processWsMessage]);

  // Load existing messages when connecting to existing run
  const loadExistingMessages = useCallback(async (currentRunId) => {
    try {
      const response = await fetch(`/api/bill-processing-ws/workflow/messages/${currentRunId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();

      if (result.success && result.messages) {
        const formattedMessages = result.messages.map((msg, idx) => {
          let isUIComponent = false;
          let content = msg.content;

          if (msg.message_type === 'workflow_step') {
            try {
              const parsed = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
              if (parsed?.status === 'ui_ready' && parsed?.component_definition) {
                content = parsed;
                isUIComponent = true;
              }
            } catch (e) {}
          }

          // FIX: message_id can be empty string '' from backend - treat as missing
          const messageId = msg.message_id && msg.message_id.trim() !== ''
            ? msg.message_id
            : `http_${idx}_${msg.timestamp || Date.now()}`;

          return {
            id: messageId,
            type: msg.message_type,
            content: content,
            timestamp: msg.timestamp,
            step_name: msg.step_name,
            tool_name: msg.tool_name,
            isUIComponent
          };
        });

        // Mark initial load complete before setting messages
        initialLoadCompleteRef.current = true;
        setMessages(formattedMessages);
        console.log(`✅ BillWS: HTTP loaded ${formattedMessages.length} messages`);
      }
    } catch (err) {
      console.error('Error loading existing messages:', err);
    }
  }, [token]);

  // Initialize on mount or run_id change
  useEffect(() => {
    if (!token) return;

    if (initialRunId) {
      setExecutionStatus('running');
      setIsLoading(true);
      setMessages([]);

      // Load existing messages then check status and connect WebSocket
      const initializeWorkflow = async () => {
        console.log('🚀 BillWS: initializeWorkflow starting for runId:', initialRunId);

        try {
          await loadExistingMessages(initialRunId);
          console.log('✅ BillWS: loadExistingMessages completed');
        } catch (loadError) {
          console.error('❌ BillWS: loadExistingMessages failed:', loadError);
        }

        // Fetch workflow status to check if already completed
        console.log('📡 BillWS: About to fetch status from:', `/api/bill-processing-ws/workflow/status/${initialRunId}`);
        try {
          const response = await fetch(`/api/bill-processing-ws/workflow/status/${initialRunId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          console.log('📡 BillWS: Status response received, status code:', response.status);
          const statusResult = await response.json();
          console.log('📡 BillWS: Status result:', JSON.stringify(statusResult).substring(0, 200));

          if (statusResult.success) {
            // Status is at top level, not inside data
            const status = statusResult.status;
            const currentStepFromAPI = statusResult.current_step;
            console.log('📊 BillWS: Workflow status on load:', status, 'step:', currentStepFromAPI);

            // If workflow is already completed or failed, don't keep loading
            if (status === 'completed' || status === 'failed') {
              setExecutionStatus(status);
              setIsLoading(false);
              console.log('✅ BillWS: Workflow already', status, '- stopping loading indicator');
              // Still connect WebSocket to get any final messages
            } else if (status === 'awaiting_user') {
              setExecutionStatus('awaiting_user');
              setIsLoading(false);
              if (currentStepFromAPI) {
                setCurrentStep(currentStepFromAPI);
              }
              console.log('✅ BillWS: Workflow awaiting_user - stopping loading indicator');
            }
          }
        } catch (err) {
          console.error('Error fetching workflow status:', err);
          // On error, still stop loading to avoid infinite spinner
          setIsLoading(false);
        }

        // Connect WebSocket for real-time updates
        connectWebSocket(initialRunId);
      };

      initializeWorkflow();
    } else {
      setMessages([]);
      setExecutionStatus('ready');
    }

    return () => {
      if (wsRef.current) wsRef.current.close(1000, 'Cleanup');
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, [token, initialRunId, connectWebSocket, loadExistingMessages]);

  // NOTE: No polling - pure WebSocket approach
  // Messages and status updates come via WebSocket
  // On reconnect, send_existing_messages sends all messages + current status

  // Send message / Start or continue workflow
  const sendMessage = useCallback(async (messageText) => {
    if (!messageText.trim() || !token) return;
    if (!runId && isLoading) return;

    setIsLoading(true);
    setError(null);

    // Add user message immediately
    const userMessage = {
      id: `user_${Date.now()}`,
      type: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);

    try {
      if (!runId) {
        // Start new workflow
        console.log('🚀 BillWS Starting workflow');
        const response = await fetch('/api/bill-processing-ws/workflow/start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            workflow_id: parseInt(workflowId),
            user_message: messageText
          })
        });
        const result = await response.json();

        if (result.success && result.run_id) {
          console.log('✅ BillWS Workflow started:', result.run_id);
          setRunId(result.run_id);
          setExecutionStatus('running');
          connectWebSocket(result.run_id);
        } else {
          throw new Error(result.error || 'Failed to start workflow');
        }
      } else {
        // Continue existing workflow
        console.log('🔄 BillWS Continuing workflow');
        const response = await fetch(`/api/bill-processing-ws/workflow/continue/${runId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ user_message: messageText })
        });
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Failed to continue workflow');
        }
        setExecutionStatus('running');
      }
    } catch (err) {
      console.error('BillWS Error:', err);
      setError(err.message);
      setExecutionStatus('failed');
      setIsLoading(false);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        type: 'error',
        content: `Error: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    }
  }, [workflowId, token, runId, isLoading, connectWebSocket]);

  // Reset workflow
  const resetWorkflow = useCallback(() => {
    if (wsRef.current) wsRef.current.close(1000, 'Reset');
    if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

    setRunId(null);
    setMessages([]);
    setExecutionStatus('ready');
    setError(null);
    setIsLoading(false);
    setWsConnected(false);
    setCurrentStep(null);
    initialLoadCompleteRef.current = false;  // Reset initial load flag
  }, []);

  // Get workflow status
  const getStatus = useCallback(async () => {
    if (!runId || !token) return null;
    try {
      const response = await fetch(`/api/bill-processing-ws/workflow/status/${runId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return await response.json();
    } catch (err) {
      console.error('Error getting status:', err);
      return null;
    }
  }, [runId, token]);

  return {
    // State
    messages,
    isLoading,
    runId,
    executionStatus,
    currentStep,
    error,
    wsConnected,
    initialLoadComplete: initialLoadCompleteRef.current,  // For auto-scroll control

    // Actions
    sendMessage,
    getStatus,
    resetWorkflow,

    // Computed
    hasActiveWorkflow: !!runId,
    canSendMessage: !isLoading && !!token,
    connectionMode: 'websocket'
  };
};
